/**
 * QuickTagChips - Smart tag chips for quick selection (multiple)
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMoodMapStore } from '../../stores/moodmapStore';
import { Plus, X, Check } from 'lucide-react';

const QuickTagChips = ({ category, selectedTagIds = [], onToggle, onSubmit }) => {
  const { tags, getSmartTags, createTag } = useMoodMapStore();
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagEmoji, setNewTagEmoji] = useState('');

  // Get smart tag suggestions for the current category
  const smartTags = getSmartTags(category, 12);

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    try {
      const tag = await createTag({
        name: newTagName.trim(),
        emoji: newTagEmoji || null,
        category: category,
      });
      onToggle(tag.id);
      setIsCreating(false);
      setNewTagName('');
      setNewTagEmoji('');
    } catch (error) {
      console.error('Error creating tag:', error);
    }
  };

  const selectedCount = selectedTagIds.length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {smartTags.map((tag) => {
          const isSelected = selectedTagIds.includes(tag.id);

          return (
            <motion.button
              key={tag.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onToggle(tag.id)}
              className={`
                px-3 py-1.5 rounded-full text-sm
                transition-colors flex items-center gap-1.5
                ${
                  isSelected
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }
                ${tag.is_system ? '' : 'border border-gray-700'}
              `}
            >
              {tag.emoji && <span>{tag.emoji}</span>}
              <span>{tag.name}</span>
              {isSelected && <Check className="w-3 h-3" />}
              {tag.usage_count > 0 && !isSelected && (
                <span className="text-xs text-gray-500 ml-1">
                  {tag.usage_count}
                </span>
              )}
            </motion.button>
          );
        })}

        {/* Create new tag button */}
        <AnimatePresence mode="wait">
          {isCreating ? (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              className="flex items-center gap-1"
            >
              <input
                type="text"
                value={newTagEmoji}
                onChange={(e) => setNewTagEmoji(e.target.value)}
                placeholder="😊"
                className="w-10 px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-center text-sm focus:outline-none focus:border-purple-500"
                maxLength={2}
              />
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateTag()}
                placeholder="Yeni etiket..."
                className="w-28 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-purple-500"
                autoFocus
              />
              <button
                onClick={handleCreateTag}
                disabled={!newTagName.trim()}
                className="p-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 rounded-lg transition-colors"
              >
                <Check className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setNewTagName('');
                  setNewTagEmoji('');
                }}
                className="p-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </motion.div>
          ) : (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsCreating(true)}
              className="px-3 py-1.5 rounded-full text-sm bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors flex items-center gap-1.5 border border-dashed border-gray-700"
            >
              <Plus className="w-3 h-3" />
              <span>Yeni</span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Submit button - shows when tags are selected */}
      {selectedCount > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="flex items-center justify-between"
        >
          <span className="text-sm text-gray-400">
            {selectedCount} etiket seçildi
          </span>
          <button
            onClick={onSubmit}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ekle</span>
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default QuickTagChips;
